/**
 * How long a DM send request may hang before it is treated as failed.
 *
 * MEASURED DEFECT (stg-0921-chat-and-notifications-two-user-staging-cases,
 * dev, 2026-09-21 — case-147-chat.mjs, plus a deterministic route-interception
 * repro): with the send request to `POST /api/v1/dm/conversations/{id}/messages/`
 * held open and never answered, the Composer's Send control stayed disabled
 * (`isSending` mirrors `useSendMessage(...).isLoading`, which react-query only
 * clears when the mutation promise SETTLES) for the full 120-second observation
 * window with ZERO error, ZERO "Not delivered · Retry" state, and no way to
 * send anything else in the thread short of a full page reload.
 *
 * This is the exact same defect class `loginTimeout.js` already fixed for the
 * sign-in button (see that file's header comment, `LOGIN_TIMEOUT_MS`) — a
 * request that never settles is worse than one that errors, because
 * `useSendMessage`'s existing `onError` (already wired to flip the optimistic
 * bubble to the "failed"/"Not delivered · Retry" state, `src/services/chat.js`)
 * never runs if the promise never rejects. Axios turns an exceeded `timeout`
 * into a rejection, which reaches that same `onError` — no new error-handling
 * branch, no new copy, just a bounded wait.
 *
 * 20s matches `LOGIN_TIMEOUT_MS`'s own reasoning: generous enough that a real,
 * slow-but-alive send is never mistaken for a hang, short enough that a user
 * is told something well inside the "did this just eat my message" window.
 */
export const CHAT_SEND_TIMEOUT_MS = 20000;

/**
 * Same defect, the ATTACHMENT (multipart) send path — deliberately a separate,
 * longer bound rather than reusing `CHAT_SEND_TIMEOUT_MS`: an upload can
 * legitimately take longer than a text-only send (up to
 * `CHAT_MEDIA_MAX_UPLOAD_BYTES` = 25MB, messaging/api/v1/views.py, over a real
 * user's upload bandwidth), so 20s would abort some genuinely-in-progress
 * uploads, trading the hang bug for a new false-timeout bug. 60s still bounds
 * a true hang; a real 25MB upload completes well inside it on any connection
 * that isn't already failing.
 */
export const CHAT_SEND_ATTACHMENT_TIMEOUT_MS = 60000;
